// Thin wrapper around postcodes.io — used to validate a UK postcode AND resolve
// its lat/lng before any backend call, so bad data never reaches the server.
// Free service, no auth. Rate limit ~600 req/min/IP — fine at our scale.

const BASE = "https://api.postcodes.io/postcodes";
const DEFAULT_TIMEOUT_MS = 5000;

function withTimeout(promise, ms) {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error("Postcode lookup timed out")), ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * @param {string} postcode  raw user input — spacing/case tolerated
 * @returns {Promise<{ok: true, postcode: string, latitude: number, longitude: number, admin_district?: string} | {ok: false, reason: string}>}
 */
export async function lookupPostcode(postcode) {
    if (!postcode || typeof postcode !== "string") {
        return { ok: false, reason: "Please enter a postcode." };
    }
    const cleaned = postcode.replace(/\s+/g, "").toUpperCase();
    if (cleaned.length < 5 || cleaned.length > 8) {
        return { ok: false, reason: "That doesn't look like a valid UK postcode." };
    }
    try {
        const res = await withTimeout(
            fetch(`${BASE}/${encodeURIComponent(cleaned)}`, { method: "GET" }),
            DEFAULT_TIMEOUT_MS,
        );
        if (res.status === 404) {
            return { ok: false, reason: "That postcode is not active. Please check and try again." };
        }
        if (!res.ok) {
            return { ok: false, reason: "Could not verify postcode. Please try again." };
        }
        const json = await res.json();
        if (json?.status !== 200 || !json?.result) {
            return { ok: false, reason: "That postcode is not active. Please check and try again." };
        }
        const { postcode: canonical, latitude, longitude, admin_district } = json.result;
        if (typeof latitude !== "number" || typeof longitude !== "number") {
            return { ok: false, reason: "Could not verify postcode. Please try again." };
        }
        return { ok: true, postcode: canonical, latitude, longitude, admin_district };
    } catch (err) {
        return {
            ok: false,
            reason:
                err?.message === "Postcode lookup timed out"
                    ? "Could not verify postcode. Please check your connection and try again."
                    : "Could not verify postcode. Please try again.",
        };
    }
}
